/**
 * **`sitrepStatus`** — the victory-condition read seam. **Its signature is FROZEN**: the
 * autoresolution runner injects it as a `terminationCheck` and its outcome taxonomy
 * carries `draw` first-class to match `winner: Side | "draw"`. Do not change the shape.
 *
 * Two branches:
 *   - **`state.sitrep` UNSET → the deathmatch verdict.** Decided iff an allegiance
 *     has {@link sideEliminated no living actor} (`../liveness.ts`). Both sides wiped
 *     at once (a mutual-kill AoE, or a totally empty pre-deploy board) reads `"draw"` —
 *     picking an arbitrary "surviving" side when neither survived would be a worse
 *     answer than the type already affords.
 *   - **`state.sitrep` SET → dispatch by `sitrep.type`.** A set sitrep runs to its own
 *     `roundLimit` regardless of deathmatch elimination — the deathmatch branch is ONLY
 *     for the unset case, so a Control fight where one side is wiped mid-round 3 still
 *     reads `"active"` until round 6's boundary, since Control's own predicate doesn't
 *     consult liveness at all, only `sitrepProgress` scores and the round cap.
 *     Escort/Extraction's {@link extractionStatus} reads the objective
 *     (`sitrep.objectiveId`) against the extraction zone + adjacency, continuously (not
 *     just at the cap) — see that module's doc for the full branch order.
 *
 * **The homebrew seventh type, `clear`** joins the dispatch as one more arm and changes
 * nothing above it. It is the odd one out twice over: its predicate ({@link clearStatus})
 * reads *terrain/edge/object* state inside its zones rather than occupancy or a tracked
 * combatant, and it has **no round cap at all** (enemy waves are endless, so the
 * clearance read is the whole clock). Its `roundLimit` is carried but never consulted,
 * and its status carries no `roundsRemaining`. Callers that render a countdown must
 * therefore treat `roundsRemaining === undefined` as "no clock", not "zero rounds left".
 */
import type { EncounterState, Side } from "@lancer-vtt/domain";
import { sideEliminated } from "../liveness";
import { controlStatus } from "./predicates/control";
import { holdoutStatus } from "./predicates/holdout";
import { gauntletStatus } from "./predicates/gauntlet";
import { reconStatus } from "./predicates/recon";
import { extractionStatus } from "./predicates/extraction";
import { clearStatus } from "./predicates/clear";

/** The victory-condition read. Frozen shape — do not change. */
export type SitrepStatus = {
  phase: "active" | "decided";
  winner?: Side | "draw";
  scores?: Record<Side, number>;
  roundsRemaining?: number;
};

/** Pure derived read: is the encounter's fight decided, and if so, who won? See module doc for the two branches. */
export function sitrepStatus(state: EncounterState): SitrepStatus {
  const { sitrep } = state;
  if (!sitrep) {
    const pcGone = sideEliminated(state, "pc");
    const npcGone = sideEliminated(state, "npc");
    if (pcGone && npcGone) return { phase: "decided", winner: "draw" };
    if (pcGone) return { phase: "decided", winner: "npc" };
    if (npcGone) return { phase: "decided", winner: "pc" };
    return { phase: "active" };
  }

  switch (sitrep.type) {
    case "clear":
      // The homebrew seventh sitrep: the only predicate that reads terrain rather than
      // occupancy/an objective combatant, and the only one with NO round cap (waves are
      // endless, the clearance read is the whole clock), so it returns no
      // `roundsRemaining`.
      return clearStatus(state, sitrep);
    case "control":
      return controlStatus(state, sitrep);
    case "holdout":
      return holdoutStatus(state, sitrep);
    case "gauntlet":
      return gauntletStatus(state, sitrep);
    case "recon":
      return reconStatus(state, sitrep);
    case "escort":
    case "extraction":
      // The objective-extraction victory read, shared by both types (their round-cap
      // differs, the logic doesn't). See `predicates/extraction.ts`.
      return extractionStatus(state, sitrep);
  }
}
